import { PUBLIC_ORGANIZATION_ID } from '../ent/root-entity-ids';
import { ROUTE } from './route-utils';
// User type for display purposes
type ZeroUser = {
  firstName?: string | null;
  lastName?: string | null;
  primaryEmailAddress?: string | null;
};

export interface AddCreateMenuItem {
  readonly id: string;
  readonly disabled?: boolean;
  readonly label?: string;
  readonly i18n?: string;
  readonly onClick?: () => void;
  readonly children?: AddCreateMenuItem[];
}

export enum AddCreateMenuItemId {
  FoodAndBeverage = 'foodAndBeverage',
  Communication = 'communication',
  Team = 'team',
  CreateChannel = 'createChannel',
  CreateDirectMessage = 'createDirectMessage',
  Add86Item = 'add86Item',
  CreateBeverageMenu = 'createBeverageMenu',
  CreateFoodMenu = 'createFoodMenu',
  ImportBeverageMenu = 'importBeverageMenu',
  ImportFoodMenu = 'importFoodMenu',
  CreateRecipe = 'createRecipe',
  CreateArticle = 'createArticle',
  AddToManagerLog = 'addToManagerLog',
  InviteEmployee = 'inviteEmployee'
}

/** Name parts as an Ent stores them (`first_name`) or as Zero syncs them (`firstName`). */
export type PersonNameParts = {
  firstName?: string | null;
  lastName?: string | null;
  first_name?: string | null;
  last_name?: string | null;
};

export type FormatPersonNameOptions<Fallback extends string | null> = {
  /** Shown when no name part is set: an email, 'Unknown', '' or null. */
  fallback: Fallback;
  /**
   * Trim each part and skip blank ones (the default). The helpers that
   * predate this one joined the parts exactly as stored; they pass `false`
   * so their output is unchanged for names with stray whitespace.
   */
  trim?: boolean;
};

/**
 * "First Last" from whichever name fields `person` carries, else the
 * caller's fallback. Every place that shows a person's name goes through
 * here; only the fallback differs per surface.
 */
export function formatPersonName<Fallback extends string | null>(
  person: PersonNameParts | null | undefined,
  options: FormatPersonNameOptions<Fallback>
): string | Fallback {
  const parts = [
    person?.firstName ?? person?.first_name,
    person?.lastName ?? person?.last_name
  ];
  const name = parts
    .map(part => (options.trim === false ? part : part?.trim()))
    .filter(Boolean)
    .join(' ');
  return name || options.fallback;
}

export function getUserDisplayName(user: ZeroUser): string {
  return formatPersonName(user, {
    fallback: user?.primaryEmailAddress || 'Unknown',
    trim: false
  });
}

/** Up to two letters for avatar fallbacks. */
export function getUserInitials(user: ZeroUser): string {
  const initials = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .map(part => part!.charAt(0))
    .join('');
  return (initials || getUserDisplayName(user).charAt(0)).toUpperCase();
}

export type AddCreateMenuCallbacks = {
  onCreateChannel?: () => void;
  onCreateDirectMessage?: () => void;
  onAdd86Item?: () => void;
  onCreateBeverageMenu?: () => void;
  onCreateFoodMenu?: () => void;
  onImportBeverageMenu?: () => void;
  onImportFoodMenu?: () => void;
  onCreateRecipe?: () => void;
  onCreateArticle?: () => void;
  onAddToManagerLog?: () => void;
  onInviteEmployee?: () => void;
};

export function getAddCreateMenu(
  t: any,
  callbacks: AddCreateMenuCallbacks = {}
): AddCreateMenuItem[] {
  return [
    {
      id: AddCreateMenuItemId.FoodAndBeverage,
      label: t('navigation.library'),
      children: [
        {
          id: AddCreateMenuItemId.Add86Item,
          label: t('eighty-six.add-item'),
          onClick: () => callbacks.onAdd86Item?.()
        },
        {
          id: AddCreateMenuItemId.CreateBeverageMenu,
          label: t('navigation.add-beverage-menu'),
          onClick: () => callbacks.onCreateBeverageMenu?.()
        },
        {
          id: AddCreateMenuItemId.CreateFoodMenu,
          label: t('navigation.add-food-menu'),
          onClick: () => callbacks.onCreateFoodMenu?.()
        },
        {
          id: AddCreateMenuItemId.ImportBeverageMenu,
          label: t('menu-import.title-beverage'),
          onClick: () => callbacks.onImportBeverageMenu?.()
        },
        {
          id: AddCreateMenuItemId.ImportFoodMenu,
          label: t('menu-import.title-food'),
          onClick: () => callbacks.onImportFoodMenu?.()
        },
        {
          id: AddCreateMenuItemId.CreateRecipe,
          label: t('navigation.create-recipe'),
          onClick: () => callbacks.onCreateRecipe?.()
        },
        {
          id: AddCreateMenuItemId.CreateArticle,
          label: t('knowledge-article.new-article'),
          onClick: () => callbacks.onCreateArticle?.()
        }
      ]
    },
    { id: 'separator' },
    {
      id: AddCreateMenuItemId.Communication,
      label: t('navigation.messages'),
      children: [
        {
          id: AddCreateMenuItemId.CreateChannel,
          label: t('messages.create-channel'),
          onClick: callbacks.onCreateChannel
        },
        {
          id: AddCreateMenuItemId.CreateDirectMessage,
          label: t('messages.create-direct-message'),
          onClick: callbacks.onCreateDirectMessage
        },
        {
          id: AddCreateMenuItemId.AddToManagerLog,
          label: t('logbook.new-draft'),
          onClick: () => callbacks.onAddToManagerLog?.()
        }
      ]
    },
    { id: 'separator' },
    {
      id: AddCreateMenuItemId.Team,
      label: t('navigation.team'),
      children: [
        {
          id: AddCreateMenuItemId.InviteEmployee,
          label: t('navigation.invite-employee'),
          onClick: () => callbacks.onInviteEmployee?.()
        }
      ]
    }
  ];
}

export type ContextualAddScope =
  'all' | 'none' | readonly AddCreateMenuItemId[];

function organizationRoute(pathname: string, organizationId: string): string {
  const prefix = `/${organizationId}/`;
  const route = pathname.startsWith(prefix)
    ? pathname.slice(prefix.length)
    : pathname.replace(/^\//, '');
  return route.replace(/\/+$/, '');
}

/** Contextual actions; the chrome decides which surfaces show the trigger. */
export function getContextualAddCreateItemIds(
  pathname: string,
  organizationId: string
): ContextualAddScope {
  const route = organizationRoute(pathname, organizationId);
  if (route.startsWith(`${ROUTE.MESSAGES}/`))
    return [
      AddCreateMenuItemId.CreateChannel,
      AddCreateMenuItemId.CreateDirectMessage
    ];

  switch (route) {
    case '':
    case ROUTE.HOME:
      return 'none';
    case ROUTE.EIGHTY_SIX:
      return [AddCreateMenuItemId.Add86Item];
    case ROUTE.TEAM:
    case ROUTE.EMPLOYEES:
      return [AddCreateMenuItemId.InviteEmployee];
    case ROUTE.LIBRARY:
      return [
        AddCreateMenuItemId.CreateRecipe,
        AddCreateMenuItemId.CreateBeverageMenu,
        AddCreateMenuItemId.CreateFoodMenu
      ];
    case `${ROUTE.LIBRARY}/${ROUTE.LIBRARY_MENUS}`:
      return [
        AddCreateMenuItemId.CreateBeverageMenu,
        AddCreateMenuItemId.CreateFoodMenu
      ];
    case `${ROUTE.LIBRARY}/${ROUTE.LIBRARY_REFERENCE}`:
    case `${ROUTE.LIBRARY}/${ROUTE.LIBRARY_REFERENCE}/manage`:
      return [AddCreateMenuItemId.CreateArticle];
    case `${ROUTE.BEVERAGES}/${ROUTE.RECIPES}`:
      return 'none';
    case `${ROUTE.BEVERAGES}/${ROUTE.COCKTAIL_MENUS}`:
      return [
        AddCreateMenuItemId.CreateBeverageMenu,
        AddCreateMenuItemId.ImportBeverageMenu
      ];
    case `${ROUTE.FOOD}/${ROUTE.FOOD_MENUS}`:
      return [
        AddCreateMenuItemId.CreateFoodMenu,
        AddCreateMenuItemId.ImportFoodMenu
      ];
    case ROUTE.MESSAGES:
      return [
        AddCreateMenuItemId.CreateChannel,
        AddCreateMenuItemId.CreateDirectMessage
      ];
    case ROUTE.LOGBOOK:
      return [AddCreateMenuItemId.AddToManagerLog];
    default:
      return 'none';
  }
}

export function filterAddCreateMenu(
  items: readonly AddCreateMenuItem[],
  scope: ContextualAddScope
): AddCreateMenuItem[] {
  if (scope === 'none') return [];

  const allowed = scope === 'all' ? null : new Set<string>(scope);
  const groups = items
    .map(item => {
      if (item.id === 'separator' || !item.children) return item;
      const children = allowed
        ? item.children.filter(child => allowed.has(child.id))
        : item.children;
      return { ...item, children };
    })
    .filter(
      item => item.id === 'separator' || (item.children?.length ?? 0) > 0
    );

  return groups.filter((item, index, arr) => {
    if (item.id !== 'separator') return true;
    const prev = arr[index - 1];
    const next = arr[index + 1];
    return Boolean(
      prev && next && prev.id !== 'separator' && next.id !== 'separator'
    );
  });
}

export function addCreateMenuLeaves(
  items: readonly AddCreateMenuItem[]
): AddCreateMenuItem[] {
  return items.flatMap(item => item.children ?? []);
}

export interface UserMetadata {
  version: number;
  default_organization_id: string | undefined;
  organization_ids: string[]; // IDs of all organizations a user belongs to
  welcome_completed_at?: string;
}

export const EMPTY_USER_METADATA: UserMetadata = {
  version: 0,
  default_organization_id: undefined,
  organization_ids: [PUBLIC_ORGANIZATION_ID]
};

/** Orgs that are not the shared public catalog. */
export function workspaceOrganizationIds(
  organizationIds: readonly string[]
): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const id of organizationIds) {
    if (id === PUBLIC_ORGANIZATION_ID || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

/**
 * Workspace to route into: honor `defaultOrganizationId` when it is a real
 * membership, otherwise the first workspace org. Public-catalog membership
 * alone is not a workspace.
 */
export function resolveWorkspaceOrganizationId(
  organizationIds: readonly string[],
  defaultOrganizationId: string | undefined
): string | undefined {
  const workspaces = workspaceOrganizationIds(organizationIds);
  if (defaultOrganizationId && workspaces.includes(defaultOrganizationId)) {
    return defaultOrganizationId;
  }
  return workspaces[0];
}

export function hasWorkspaceMembership(metadata: UserMetadata): boolean {
  return Boolean(
    resolveWorkspaceOrganizationId(
      metadata.organization_ids,
      metadata.default_organization_id
    )
  );
}
